import { Injectable } from '@angular/core';
import { BehaviorSubject, from, Observable, Subject } from 'rxjs';
import { first, takeWhile } from 'rxjs/operators';

import { WebApiModel } from '../../models/web-api.model';
import { GoogleMapLoaderService } from '../../services/google-map-loader-service';

// declare var google: any;

@Injectable()
export class MapService {
  constructor(private mapLoaderService: GoogleMapLoaderService) {}

  setMapZoom(map: any, locations: any[], userLatLng: any) {
    let currentLat = 0;
    let currentLng = 0;
    let numberOfUniqueLocations = 0;
    let minLocationDistance = 0;
    let maxLocationDistance = 0;
    let closestLatLng: any;
    let farthestLatLng: any;

    const latlngbounds = new google.maps.LatLngBounds();

    for (const location of locations) {
      const currentLocation: WebApiModel.Location = location;

      // If there are multiple locations with the same coordinates then skip that location, otherwise
      // map bounds are not set properly below. The zoom is set to fit bounds instead of center and the map
      // is zoomed in too much, it was happening on the person profile pages. When doctor is working at
      // multiple facilities which were at the same address.
      if (currentLocation.longitude === currentLng && currentLocation.latitude === currentLat) {
        continue;
      }

      if (userLatLng) {
        const currentLatLng = new google.maps.LatLng(currentLocation.latitude, currentLocation.longitude);

        const locationDistance = google.maps.geometry.spherical.computeDistanceBetween(userLatLng, currentLatLng);
        if (locationDistance < minLocationDistance || minLocationDistance === 0) {
          minLocationDistance = locationDistance;
          closestLatLng = currentLatLng;
        }
        if (locationDistance > maxLocationDistance || maxLocationDistance === 0) {
          maxLocationDistance = locationDistance;
          farthestLatLng = currentLatLng;
        }
      }

      if (currentLocation.longitude !== 0 && currentLocation.latitude !== 0) {
        latlngbounds.extend(new google.maps.LatLng(currentLocation.latitude, currentLocation.longitude));
        numberOfUniqueLocations++;
      }

      currentLat = currentLocation.latitude;
      currentLng = currentLocation.longitude;
    }

    let initialZoom = true;

    // Listener to handle fitBounds zoom changes
    // When there are two marker located very close on the map and we use fitBounds then the map
    // will zoom in too much, setZoom right after fitBounds is not working but fitBounds will
    // fire zoom_changed and bounds_changed events, this is how we zoom the map back a little
    google.maps.event.addListener(map, 'zoom_changed', () => {
      const zoomChangeBoundsListener = google.maps.event.addListener(map, 'bounds_changed', () => {
        // double check after angular 7 upgrade
        if (map.getZoom() > 19 && initialZoom === true) {
          // Change max/min zoom here
          map.setZoom(19);
        }

        initialZoom = false;
        google.maps.event.removeListener(zoomChangeBoundsListener);
      });
    });

    if (userLatLng) {
      map.fitBounds(new google.maps.LatLngBounds().extend(userLatLng).extend(farthestLatLng), 0); // .extend(closestLatLng));
      map.setZoom(map.getZoom() - 1);
    } else {
      if (numberOfUniqueLocations > 1) {
        map.fitBounds(latlngbounds, 0);
        // Zoom level after fitBounds is handled by the zoom_changed event listener above.
        // setZoom() didn't work after fitBounds.
      } else if (numberOfUniqueLocations === 1) {
        map.setCenter(new google.maps.LatLng(locations[0].latitude, locations[0].longitude));
        map.setZoom(14); // Set zoom level for a single location, otherwise the default is a bit too far.
      }
    }
  }

  // Sets default view on the map with NZ showing
  setDefaultMap(map: any) {
    const country = 'New Zealand';
    const geocoder = new google.maps.Geocoder();
    void geocoder.geocode({ address: country }, (results, status) => {
      if (status === google.maps.GeocoderStatus.OK) {
        map.setCenter(results[0].geometry.location);
        map.setZoom(4);
      }
    });
  }

  // Retrieve the latitude/longitude for an address
  // Returns a promise which can be awaited
  getLocationFromAddress(address: string) {
    return this.mapLoaderService.load().then(() => {
      const geocoder = new google.maps.Geocoder();
      const ob = Observable.create((observer) =>
        geocoder.geocode({ address }, (results, status) => {
          if (status === google.maps.GeocoderStatus.OK) {
            const loc = results[0].geometry.location;
            observer.next({
              lat: loc.lat(),
              lng: loc.lng(),
            });
          } else {
            observer.next(null);
          }
        }),
      ) as Observable<{ lat: number; lng: number }>;

      return ob.pipe(first()).toPromise();
    });
  }
}
